<template>
  <div
    class="to-step-button"
    :class="[
      { 'to-step-button--disabled': isCurrentStep },
    ]"
    :data-name-aria="nameAria"
  >
    <button
      class="to-step-button__button"
      @click="handleClick"
      :disabled="isCurrentStep || disabled"
    >
      <span class="to-step-button__loader" v-if="isPending">
        <svg version="1.1" id="L2" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" x="0px"
          y="0px" viewBox="0 0 100 100" enable-background="new 0 0 100 100" xml:space="preserve">
          <circle fill="none" stroke="currentColor" stroke-width="4" stroke-miterlimit="10" cx="50" cy="50" r="48" />
          <line fill="none" stroke-linecap="round" stroke="currentColor" stroke-width="4" stroke-miterlimit="10" x1="50"
            y1="50" x2="85" y2="50.5">
            <animateTransform attributeName="transform" dur="2s" type="rotate" from="0 50 50" to="360 50 50"
              repeatCount="indefinite" />
          </line>
          <line fill="none" stroke-linecap="round" stroke="currentColor" stroke-width="4" stroke-miterlimit="10" x1="50"
            y1="50" x2="49.5" y2="74">
            <animateTransform attributeName="transform" dur="15s" type="rotate" from="0 50 50" to="360 50 50"
              repeatCount="indefinite" />
          </line>
        </svg>
      </span>
      <slot></slot>
    </button>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, type Ref } from 'vue';
import pinia from '../../core/pinia';
import { useStepsStore } from '../../stores';
import { E_STEP_BUTTON_EVENT } from '../../types/components-events.enums';
import type { TStepButtonEventResponse } from '../../types/event-response.types';
import emitter from '../../core/emitter';
import { E_MIDDLEWARE_STEPS } from '../../types/middlewares.types';

interface IProps {
  disabled?: boolean;
  nameAria: string;
  to: string;
}
const props = defineProps<IProps>();
const emit = defineEmits<{
  (e: E_STEP_BUTTON_EVENT.CLICK, payload: TStepButtonEventResponse[E_STEP_BUTTON_EVENT.CLICK]): TStepButtonEventResponse;
}>();

const stepsStore = useStepsStore(pinia);

const isPending: Ref<boolean> = ref(false);

const isCurrentStep = computed(() => {
  return stepsStore.getCurrentStep?.toLowerCase() === props.to?.toLowerCase()
});

emitter.on('to-step-button:load-start', (step) => {
  const stepName = step as string;
  if (stepName === props.to) {
    isPending.value = true;
  }
});
emitter.on('to-step-button:load-end', (step) => {
  const stepName = step as string;
  if (stepName === props.to) {
    isPending.value = false;
  }
});

function handleClick() {
  emitter.emit(E_MIDDLEWARE_STEPS.CHANGE, props.to);
  emit(E_STEP_BUTTON_EVENT.CLICK, props.to);
}
</script>

<style scoped lang="scss">
.to-step-button {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;

  &__loader {
    display: flex;
    align-items: center;
    justify-content: center;
    color: #ffffff;
    width: 12px;
    height: 12px;
    padding-bottom: 2px;
  }

  &__button {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    font-family: 'Bebas Neue', sans-serif;
    font-weight: 400;
    font-size: 16px;
    line-height: 16px;
    letter-spacing: 0.5px;
    text-align: center;
    vertical-align: middle;
    color: #ffffff;
    width: 100%;
    padding: 12px 14px;
    border: none;
    background: $brand-color;
    cursor: pointer;
    text-transform: uppercase;
    transition: all .3s ease;

    &:disabled {
      background: #E6E6E6;
      color: #B2B2B2;
      cursor: default;
    }
  }
}
</style>